import { Link } from 'react-router-dom'
import {
  Heart, Home as HomeIcon, CalendarDays, ShieldCheck, SlidersHorizontal, ClipboardList,
} from 'lucide-react'
import {
  Section, SectionHeading, IconCircle, NumberCircle, CheckItem, FAQ, CTABand, SplitHero, NonMedicalNote,
} from '../components/ui'
import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'

const whatItMeans = [
  'CDHCI stands for Client Directed Home Care Invoicing.',
  'It is available to Alberta home care clients who have been found eligible.',
  'Clients are assessed and approved through Alberta Health Services and their case manager.',
  'Approved clients may choose an approved home care provider such as True North Care & Support.',
  'Services are delivered according to the approved care plan and the authorized hours.',
  'Billing for authorized services is handled directly through Alberta Blue Cross.',
]

const benefits = [
  {
    icon: SlidersHorizontal,
    title: 'More choice',
    body: 'Rather than being assigned a provider, you choose the approved agency that feels right for your family.',
  },
  {
    icon: CalendarDays,
    title: 'Flexible scheduling',
    body: 'Visits are arranged around your routines and preferences, within the hours on your approved care plan.',
  },
  {
    icon: Heart,
    title: 'Personalized support',
    body: 'Care is built around the person — their history, their pace and what a good day looks like for them.',
  },
  {
    icon: ClipboardList,
    title: 'Less paperwork to manage',
    body: 'We invoice Alberta Blue Cross directly for authorized services, so there is less administration for you.',
  },
  {
    icon: ShieldCheck,
    title: 'Screened, caring caregivers',
    body: 'Every caregiver is interviewed, reference-checked and matched for temperament as well as skill.',
  },
  {
    icon: HomeIcon,
    title: 'Independence at home',
    body: 'The goal is always the same: staying safe, comfortable and independent in familiar surroundings.',
  },
]

const services = [
  'Personal care — bathing, grooming, dressing and toileting support',
  'Companionship and everyday emotional support',
  'Meal preparation and mealtime company',
  'Light housekeeping, laundry and tidying',
  'Medication reminders (non-administration)',
  'Respite care so family caregivers can rest',
  "Dementia and Alzheimer's support",
  'Palliative and end-of-life comfort care',
  'Care plan coordination and ongoing communication',
]

const steps = [
  {
    n: '1',
    title: 'Request a home care assessment',
    body: 'Contact Alberta Health Services or speak with your case manager to find out whether you qualify for publicly funded home care.',
  },
  {
    n: '2',
    title: 'Get approved for CDHCI',
    body: 'Your case manager confirms whether CDHCI suits your situation, and authorizes your care plan and the hours covered.',
  },
  {
    n: '3',
    title: 'Choose True North Care & Support',
    body: 'Tell your case manager you would like True North Care & Support as your approved provider. We are happy to help you with that conversation.',
  },
  {
    n: '4',
    title: 'Start receiving care',
    body: 'We coordinate your schedule, match a caregiver, begin visits, and invoice Alberta Blue Cross directly for authorized services.',
  },
]

const faqs = [
  {
    q: 'What does CDHCI mean?',
    a: 'CDHCI stands for Client Directed Home Care Invoicing. It is an Alberta home care option that lets eligible clients who have been assessed for publicly funded home care choose an approved provider, who can then invoice Alberta Blue Cross for the authorized services.',
  },
  {
    q: 'Is True North Care & Support an approved CDHCI provider?',
    a: 'Yes. True North Care & Support is an approved service provider for the Client Directed Home Care Invoicing program, so eligible clients can name us as their chosen provider.',
  },
  {
    q: 'Who decides if I am eligible?',
    a: 'Alberta Health Services does. A case manager assesses your needs and determines whether you qualify for home care, whether CDHCI is appropriate, and how many hours are authorized. We are not part of that decision.',
  },
  {
    q: 'Can I choose True North Care & Support as my provider?',
    a: 'If you have been approved for CDHCI, yes — simply let your case manager know that you would like True North Care & Support. Give us a call first if you would like to meet us and ask questions before you decide.',
  },
  {
    q: 'Do I have to pay out of pocket?',
    a: 'Services authorized under your approved care plan are invoiced directly to Alberta Blue Cross, not to you. If you choose additional private hours beyond what has been authorized, those are billed separately and we confirm the cost with you in writing beforehand.',
  },
  {
    q: 'What services can I receive under CDHCI?',
    a: 'Your approved care plan sets out what is covered. Our caregivers provide non-medical support such as personal care, meal preparation, light housekeeping, companionship, medication reminders, respite, and dementia or end-of-life support.',
  },
  {
    q: 'How do I get started?',
    a: 'If you already have CDHCI approval, call us and we will coordinate with your case manager. If you are not sure whether you qualify, contact Alberta Health Services for an assessment — and feel free to call us in the meantime with any questions.',
  },
]

export default function CDHCI() {
  return (
    <>
      <SplitHero
        eyebrow="Alberta home care funding"
        title="Client Directed Home Care Invoicing (CDHCI)"
        lede="True North Care & Support is an approved service provider for Alberta's Client Directed Home Care Invoicing program."
        image="/assets/how-it-works.jpg"
        imageAlt="A care coordinator reviewing a care plan with a family at the kitchen table"
        actions={
          <>
            <Link to="/schedule-assessment" className="btn btn-primary">Book a Free Consultation</Link>
            <a href={PHONE_TEL} className="btn btn-secondary">Call Us Today</a>
          </>
        }
      />

      {/* Intro */}
      <Section tone="cream">
        <SectionHeading
          eyebrow="Choice in your care"
          title="Care from a provider you chose yourself"
          lede="Through the Client Directed Home Care Invoicing program, eligible clients can choose an approved home care provider that best fits their needs, schedule and preferences. True North Care & Support is proud to be an approved provider, helping clients receive compassionate, reliable care at home while keeping the billing side simple."
        />
      </Section>

      {/* What is CDHCI */}
      <Section tone="panel">
        <SectionHeading
          eyebrow="The basics"
          title="What CDHCI is"
          lede="A short summary of how the program works, so you know what to expect before you speak with your case manager."
        />
        <div className="grid grid-2" style={{ marginTop: 48, gap: 16, maxWidth: 920 }}>
          {whatItMeans.map((item) => <CheckItem key={item}>{item}</CheckItem>)}
        </div>
      </Section>

      {/* Benefits */}
      <Section tone="white">
        <SectionHeading
          eyebrow="Why it helps"
          title="What the program means for your family"
          lede="CDHCI puts the choice of provider in your hands, and takes some of the administration off your plate."
        />
        <div className="grid grid-3" style={{ marginTop: 48 }}>
          {benefits.map((b) => (
            <div key={b.title} className="card-cream">
              <IconCircle icon={b.icon} />
              <h3 style={{ margin: '20px 0 0 0', fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>
                {b.title}
              </h3>
              <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>{b.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Services we can provide */}
      <Section tone="cream">
        <SectionHeading
          eyebrow="Under your care plan"
          title="Services we can provide"
          lede="What we deliver is guided by the care plan your case manager approves. These are the kinds of support our caregivers offer."
        />
        <div className="grid grid-2" style={{ marginTop: 48, gap: 16, maxWidth: 920 }}>
          {services.map((s) => <CheckItem key={s}>{s}</CheckItem>)}
        </div>
        <div style={{ marginTop: 48 }}>
          <NonMedicalNote />
        </div>
      </Section>

      {/* How it works */}
      <Section tone="panel">
        <SectionHeading
          eyebrow="Getting started"
          title="How CDHCI works, step by step"
          lede="Four steps from your first enquiry to your first visit."
        />
        <div className="grid grid-2" style={{ marginTop: 48, gap: 40 }}>
          {steps.map((s) => (
            <div key={s.n} style={{ display: 'flex', gap: 20 }}>
              <span style={{ marginTop: 4 }}><NumberCircle n={s.n} /></span>
              <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                <h3 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>{s.title}</h3>
                <p style={{ margin: '8px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Eligibility */}
      <Section tone="white">
        <div className="grid grid-2" style={{ gap: 48, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>
            <SectionHeading
              eyebrow="Eligibility"
              title="Who may qualify"
              lede="You may be eligible if you are an Alberta resident with a valid Alberta Health Care card, your care needs can be safely met at home, and an Alberta Health Services case manager determines that you qualify for home care services and CDHCI."
            />
            <div style={{ marginTop: 32 }}>
              <Link to="/contact" className="btn btn-secondary btn-auto">Ask us about eligibility</Link>
            </div>
          </div>
          <div className="card-cream" style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>
              An important note
            </h3>
            <p style={{ margin: '16px 0 0 0', fontSize: 17, lineHeight: 1.7, color: 'var(--color-muted)' }}>
              Eligibility, approved care hours and covered services are determined by Alberta Health Services and/or the
              client's case manager. True North Care & Support provides services according to the approved care plan.
            </p>
            <p style={{ margin: '16px 0 0 0', fontSize: 17, lineHeight: 1.7, color: 'var(--color-muted)' }}>
              If you are unsure where to begin, call us at{' '}
              <a href={PHONE_TEL} style={{ fontWeight: 600, color: 'var(--color-teal-800)' }}>{PHONE_DISPLAY}</a>{' '}
              and we will happily point you in the right direction.
            </p>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section tone="panel-deep">
        <FAQ eyebrow="Common questions" title="Questions about CDHCI" items={faqs} />
      </Section>

      <CTABand
        eyebrow="Next step"
        title="Approved for CDHCI, or exploring your options?"
        lede="Contact True North Care & Support today. We can answer your questions, explain the next steps, and help coordinate compassionate care at home."
        primaryLabel="Book a Free Consultation"
        primaryTo="/schedule-assessment"
        secondaryLabel="Contact Us"
        secondaryTo="/contact"
      />
    </>
  )
}
